// SuperAdminProfile.jsx - Complete SuperAdmin Profile with RBAC User Management
import React, { useState, useEffect, useMemo } from 'react';
import { AppHeader } from '../components/ui/layout/app-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';

import {
  User,
  Shield,
  Key,
  Mail,
  Edit,
  Save,
  X,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Crown,
  Settings,
  Database,
  Users,
  Building,
  BarChart3,
  Zap,
  Lock,
  Globe,
  Activity,
  TrendingUp,
  Plus,
  Trash2,
  UserCog,
  MoreHorizontal
} from 'lucide-react';
import { BASE_URL } from '../lib/api';
import { useToast } from '../hooks/use-toast';
// Available permissions for Admin users


// Password validation utility functions
const validatePassword = (password) => {
  const errors = [];
  let isValid = true;

  if (password.length < 12) {
    errors.push('Password must be at least 12 characters long');
    isValid = false;
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
    isValid = false;
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
    isValid = false;
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
    isValid = false;
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
    isValid = false;
  }

  return { isValid, errors };
};

const validatePasswordsMatch = (password, confirmPassword) => {
  const isValid = password === confirmPassword && password.length > 0;
  const error = !isValid && confirmPassword.length > 0 ? 'Passwords do not match' : '';
  return { isValid, error };
};

const validateCurrentPasswordDifferent = (currentPassword, newPassword) => {
  const isValid = currentPassword !== newPassword || newPassword.length === 0;
  const error = !isValid ? 'New password must be different from current password' : '';
  return { isValid, error };
};

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password, validation, showRequirements }) => {
  const getStrengthColor = () => {
    if (password.length === 0) return 'bg-gray-200';
    if (validation.isValid) return 'bg-green-500';
    if (password.length >= 8) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStrengthText = () => {
    if (password.length === 0) return 'Enter password';
    if (validation.isValid) return 'Strong password';
    if (password.length >= 8) return 'Medium strength';
    return 'Weak password';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: validation.isValid ? '100%' : `${Math.min((password.length / 12) * 100, 90)}%` }}
          />
        </div>
        <span className="text-xs text-gray-600 min-w-[100px]">{getStrengthText()}</span>
      </div>
      
      {showRequirements && password.length > 0 && (
        <div className="space-y-1">
          {validation.errors.map((error, index) => (
            <p key={index} className="text-xs text-red-600 flex items-center">
              <X className="h-3 w-3 mr-1" />
              {error}
            </p>
          ))}
          {validation.isValid && (
            <p className="text-xs text-green-600 flex items-center">
              <CheckCircle className="h-3 w-3 mr-1" />
              All requirements met
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Admin User Card Component
const AdminUserCard = ({ admin, onEdit, onDelete, onToggleStatus }) => {
  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${admin.status === 'active' ? 'bg-green-100' : 'bg-gray-100'}`}>
              <User className={`h-4 w-4 ${admin.status === 'active' ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{admin.fullName}</h3>
              <p className="text-xs text-gray-500">{admin.email}</p>
              <Badge 
                variant={admin.status === 'active' ? 'default' : 'secondary'} 
                className={`mt-1 text-xs ${admin.status === 'active' ? 'bg-green-600' : 'bg-gray-400'}`}
              >
                {admin.status === 'active' ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(admin)}>
              <Edit className="h-4 w-4" />
            </Button>
          
            <Button variant="ghost" size="sm" onClick={() => onDelete(admin)} className="text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
    </Card>
  );
};

export default function SuperAdminProfilePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // API Base URL


  // State for admin basic info
  const [adminDetails, setAdminDetails] = useState({
    fullName: '',
    email: '',
    username: '',
    role: 'SuperAdmin',
    loginTime: '',
    permissions: ['all']
  });

  // State for password change
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // State for admin users management
  const [adminUsers, setAdminUsers] = useState([]);
  const [showCreateAdminDialog, setShowCreateAdminDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [newAdminForm, setNewAdminForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    permissions: [],
    status: 'active'
  });

  // State for editing modes
  const [editingProfile, setEditingProfile] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
    newAdmin: false,
    confirmAdmin: false
  });

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  // Password validation using useMemo for performance
  const passwordValidation = useMemo(() => {
    return validatePassword(passwordForm.newPassword);
  }, [passwordForm.newPassword]);

  const passwordMatchValidation = useMemo(() => {
    return validatePasswordsMatch(passwordForm.newPassword, passwordForm.confirmPassword);
  }, [passwordForm.newPassword, passwordForm.confirmPassword]);

  const currentPasswordDifferentValidation = useMemo(() => {
    return validateCurrentPasswordDifferent(passwordForm.currentPassword, passwordForm.newPassword);
  }, [passwordForm.currentPassword, passwordForm.newPassword]);

  // New admin password validation
  const newAdminPasswordValidation = useMemo(() => {
    return validatePassword(newAdminForm.password);
  }, [newAdminForm.password]);

  const newAdminPasswordMatchValidation = useMemo(() => {
    return validatePasswordsMatch(newAdminForm.password, newAdminForm.confirmPassword);
  }, [newAdminForm.password, newAdminForm.confirmPassword]);

  // Check if password form is valid
  const isPasswordFormValid = useMemo(() => {
    return (
      passwordForm.currentPassword.trim() &&
      passwordValidation.isValid &&
      passwordMatchValidation.isValid &&
      currentPasswordDifferentValidation.isValid
    );
  }, [
    passwordForm.currentPassword,
    passwordValidation.isValid,
    passwordMatchValidation.isValid,
    currentPasswordDifferentValidation.isValid
  ]);

  // Check if new admin form is valid
  const isNewAdminFormValid = useMemo(() => {
    return (
      newAdminForm.fullName.trim() &&
      newAdminForm.email.trim() &&
      newAdminPasswordValidation.isValid &&
      newAdminPasswordMatchValidation.isValid
      // Permissions validation removed since UI doesn't have permission selection
    );
  }, [
    newAdminForm.fullName,
    newAdminForm.email,
    newAdminPasswordValidation.isValid,
    newAdminPasswordMatchValidation.isValid
  ]);

  // Load admin profile data and admin users
  useEffect(() => {
    fetchAdminData();
    loadAdminUsers();
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Get admin user data from localStorage
      const adminData = localStorage.getItem('admin_user');
      
      if (adminData) {
        const parsedData = JSON.parse(adminData);
        setAdminDetails({
          fullName: parsedData.username || 'SuperAdmin',
          email: parsedData.email || 'superadmin1413@gmail.com',
          username: parsedData.username || 'SuperAdmin',
          role: parsedData.role || 'SuperAdmin',
          loginTime: parsedData.loginTime || new Date().toISOString(),
          permissions: parsedData.permissions || ['all']
        });
      } else {
        // Fallback data
        setAdminDetails({
          fullName: 'SuperAdmin',
          email: 'superadmin1413@gmail.com',
          username: 'SuperAdmin',
          role: 'SuperAdmin',
          loginTime: new Date().toISOString(),
          permissions: ['all']
        });
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast({
        title: 'Error',
        description: 'Error loading profile data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAdminUsers = () => {
    try {
      const storedAdmins = localStorage.getItem('admin_users');
      if (storedAdmins) {
        setAdminUsers(JSON.parse(storedAdmins));
      } else {
        // Initialize with empty array if no admins exist
        setAdminUsers([]);
        localStorage.setItem('admin_users', JSON.stringify([]));
      }
    } catch (error) {
      console.error('Error loading admin users:', error);
      setAdminUsers([]);
    }
  };

  const saveAdminUsers = (users) => {
    try {
      localStorage.setItem('admin_users', JSON.stringify(users));
      setAdminUsers(users);
    } catch (error) {
      console.error('Error saving admin users:', error);
      toast({
        title: 'Error',
        description: 'Failed to save admin users data',
        variant: 'destructive'
      });
    }
  };

  const handleProfileUpdate = async () => {
    if (!adminDetails.fullName.trim()) {
      toast({
        title: 'Error',
        description: 'Name cannot be empty.',
        variant: 'destructive'
      });
      return;
    }

    setUpdating(true);
    try {
      // Update localStorage
      const currentAdminData = JSON.parse(localStorage.getItem('admin_user') || '{}');
      const updatedAdminData = {
        ...currentAdminData,
        username: adminDetails.fullName.trim(),
        fullName: adminDetails.fullName.trim()
      };
      
      localStorage.setItem('admin_user', JSON.stringify(updatedAdminData));
      
      toast({
        title: 'Success',
        description: 'Profile updated successfully!'
      });
      setEditingProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async () => {
    // Comprehensive validation checks
    if (!passwordForm.currentPassword.trim()) {
      toast({
        title: 'Error',
        description: 'Current password is required.',
        variant: 'destructive'
      });
      return;
    }

    if (!passwordForm.newPassword.trim()) {
      toast({
        title: 'Error',
        description: 'New password is required.',
        variant: 'destructive'
      });
      return;
    }

    // Check password validation
    if (!passwordValidation.isValid) {
      toast({
        title: 'Error',
        description: passwordValidation.errors[0] || 'Please check password requirements.',
        variant: 'destructive'
      });
      return;
    }

    // Check password match validation
    if (!passwordMatchValidation.isValid) {
      toast({
        title: 'Error',
        description: passwordMatchValidation.error,
        variant: 'destructive'
      });
      return;
    }

    // Check current password different validation
    if (!currentPasswordDifferentValidation.isValid) {
      toast({
        title: 'Error',
        description: currentPasswordDifferentValidation.error,
        variant: 'destructive'
      });
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('superadmin_token');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Authentication token not found. Please log in again.',
          variant: 'destructive'
        });
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      console.log('Making API call to change SuperAdmin password...');

      // Make API call to change password
      const response = await fetch(`${BASE_URL}/authentication/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Your password has been changed successfully!'
        });

        // Clear password form
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        // Reset password visibility
        setShowPasswords({
          current: false,
          new: false,
          confirm: false,
          newAdmin: false,
          confirmAdmin: false
        });
      } else {
        throw new Error(data.message || 'Password change failed');
      }

    } catch (error) {
      console.error('Error changing password:', error);
      
      // Handle authentication errors
      if (error.message.includes('token') || error.message.includes('unauthorized')) {
        toast({
          title: 'Error',
          description: 'Your session has expired. Please log in again.',
          variant: 'destructive'
        });
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to change password. Please try again.',
        variant: 'destructive'
      });

    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!isNewAdminFormValid) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields correctly.',
        variant: 'destructive'
      });
      return;
    }

    // Check if email already exists
    const emailExists = adminUsers.some(admin => admin.email.toLowerCase() === newAdminForm.email.toLowerCase());
    if (emailExists) {
      toast({
        title: 'Error',
        description: 'An admin with this email already exists.',
        variant: 'destructive'
      });
      return;
    }

    setCreatingAdmin(true);

    try {
      const token = localStorage.getItem('superadmin_token');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Authentication token not found. Please log in again.',
          variant: 'destructive'
        });
        navigate('/');
        return;
      }

      console.log('Creating subadmin via API...');

      // API Call to create subadmin
      const response = await fetch(`${BASE_URL}/authentication/superadmin/subadmins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fullName: newAdminForm.fullName.trim(),
          email: newAdminForm.email.trim().toLowerCase(),
          password: newAdminForm.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Subadmin created successfully via API:', data);

        // Save to localStorage for local display
        const newAdmin = {
          id: data.subadmin.id, // Use ID from API response
          fullName: data.subadmin.fullName,
          email: data.subadmin.email,
          role: 'Admin',
          permissions: newAdminForm.permissions,
          status: 'active', // Default status (not stored in database)
          createdAt: data.subadmin.createdAt || new Date().toISOString(),
          createdBy: adminDetails.email,
          lastLogin: null, // Not stored in database
          emailConfirmed: data.subadmin.emailConfirmed || true
        };

        const updatedAdmins = [...adminUsers, newAdmin];
        saveAdminUsers(updatedAdmins);

        toast({
          title: 'Success',
          description: `Admin ${newAdmin.fullName} created successfully!`
        });
        
        // Reset form
        setNewAdminForm({
          fullName: '',
          email: '',
          password: '',
          confirmPassword: '',
          permissions: [],
          status: 'active'
        });
        
        setShowCreateAdminDialog(false);

      } else {
        throw new Error(data.message || 'Failed to create subadmin');
      }

    } catch (error) {
      console.error('❌ Error creating admin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create admin. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setNewAdminForm({
      fullName: admin.fullName,
      email: admin.email,
      password: '',
      confirmPassword: '',
      permissions: admin.permissions,
      status: admin.status
    });
    setShowCreateAdminDialog(true);
  };

  const handleUpdateAdmin = async () => {
    if (!newAdminForm.fullName.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields.',
        variant: 'destructive'
      });
      return;
    }

    // If password is provided, validate it
    if (newAdminForm.password.trim()) {
      if (!newAdminPasswordValidation.isValid || !newAdminPasswordMatchValidation.isValid) {
        toast({
          title: 'Error',
          description: 'Please check password requirements.',
          variant: 'destructive'
        });
        return;
      }
    }

    setCreatingAdmin(true);

    try {
      const updatedAdmins = adminUsers.map(admin => {
        if (admin.id === editingAdmin.id) {
          const updatedAdmin = {
            ...admin,
            fullName: newAdminForm.fullName.trim(),
            permissions: newAdminForm.permissions,
            status: newAdminForm.status,
            updatedAt: new Date().toISOString(),
            updatedBy: adminDetails.email
          };
          
          // Update password if provided
          if (newAdminForm.password.trim()) {
            updatedAdmin.hashedPassword = btoa(newAdminForm.password);
          }
          
          return updatedAdmin;
        }
        return admin;
      });

      saveAdminUsers(updatedAdmins);

      toast({
        title: 'Success',
        description: `Admin ${newAdminForm.fullName} updated successfully!`
      });
      
      // Reset form
      setNewAdminForm({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        permissions: [],
        status: 'active'
      });
      
      setEditingAdmin(null);
      setShowCreateAdminDialog(false);

    } catch (error) {
      console.error('Error updating admin:', error);
      toast({
        title: 'Error',
        description: 'Failed to update admin. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = (admin) => {
    if (window.confirm(`Are you sure you want to delete admin "${admin.fullName}"? This action cannot be undone.`)) {
      try {
        const updatedAdmins = adminUsers.filter(a => a.id !== admin.id);
        saveAdminUsers(updatedAdmins);
        toast({
          title: 'Success',
          description: `Admin ${admin.fullName} deleted successfully.`
        });
      } catch (error) {
        console.error('Error deleting admin:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete admin. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleToggleAdminStatus = (admin) => {
    try {
      const updatedAdmins = adminUsers.map(a => {
        if (a.id === admin.id) {
          return {
            ...a,
            status: a.status === 'active' ? 'inactive' : 'active',
            updatedAt: new Date().toISOString(),
            updatedBy: adminDetails.email
          };
        }
        return a;
      });

      saveAdminUsers(updatedAdmins);
      toast({
        title: 'Success',
        description: `Admin ${admin.fullName} ${admin.status === 'active' ? 'disabled' : 'enabled'} successfully.`
      });
    } catch (error) {
      console.error('Error toggling admin status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update admin status. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('admin_user');
    navigate('/');
  };

  const handlePermissionChange = (permissionId, checked) => {
    setNewAdminForm(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(p => p !== permissionId)
    }));
  };

  if (loading) {
    return (
      <>
        <AppHeader title="SuperAdmin Profile" />
        <main className="flex-1 p-4 md:p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            <Card>
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <AppHeader title="SuperAdmin Profile" showBackButton={true} backButtonPath="/dashboard">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 hover:text-red-700">
            Logout
          </Button>
        </div>
      </AppHeader>

      <main className="flex-1 p-4 md:p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          
          <Tabs defaultValue="personal" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="personal">Personal Info</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="admin">Admin Access</TabsTrigger>
              {/* <TabsTrigger value="users">User Management</TabsTrigger> */}
            </TabsList>

            <TabsContent value="personal" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                        <Crown className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle>SuperAdmin Information</CardTitle>
                        <CardDescription>Manage your administrator account details</CardDescription>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditingProfile(!editingProfile)}
                      disabled={updating}
                    >
                      {editingProfile ? (
                        <>
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit Info
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Full Name - Editable */}
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="fullName">Display Name</Label>
                      {editingProfile ? (
                        <Input
                          id="fullName"
                          value={adminDetails.fullName}
                          onChange={(e) => setAdminDetails(prev => ({ ...prev, fullName: e.target.value }))}
                          placeholder="Enter display name"
                          className="max-w-md"
                        />
                      ) : (
                        <div className="flex items-center space-x-2 bg-gray-50 p-3 rounded-md max-w-md">
                          <User className="h-4 w-4 text-gray-400" />
                          <p className="text-sm text-gray-900">{adminDetails.fullName}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Email Address - Read Only */}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="flex items-center space-x-2 bg-gray-50 p-3 rounded-md">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-900">{adminDetails.email}</p>
                      </div>
                      <p className="text-xs text-gray-500">Email cannot be changed for security reasons</p>
                    </div>

                    {/* Role - Read Only */}
                    <div className="space-y-2">
                      <Label>Administrator Role</Label>
                      <div className="flex items-center space-x-2 bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-md border border-green-200">
                        <Shield className="h-4 w-4 text-green-600" />
                        <Badge variant="default" className="bg-green-600 text-white">
                          {adminDetails.role}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Additional Info Section */}
                  <div className="border-t pt-4 mt-6">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Status</Label>
                        <div className="flex items-center space-x-2">
                          <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-gray-700">Active Administrator</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Login</Label>
                        <p className="text-sm text-gray-700">
                          {adminDetails.loginTime ? 
                            new Date(adminDetails.loginTime).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 
                            'Current session'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {editingProfile && (
                    <div className="flex justify-end space-x-2 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setEditingProfile(false);
                          // Reset to original value if user cancels
                          fetchAdminData();
                        }}
                        disabled={updating}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleProfileUpdate}
                        disabled={updating || !adminDetails.fullName.trim()}
                      >
                        {updating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Key className="h-5 w-5" />
                    <CardTitle>Change Password</CardTitle>
                  </div>
                  <CardDescription>
                    Update your administrator password. Your new password must meet all security requirements below.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">
                      Current Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        placeholder="Enter your current password"
                        disabled={changingPassword}
                        className={`pr-10 ${
                          passwordForm.currentPassword && passwordForm.currentPassword.length > 0 
                            ? 'border-green-300' : ''
                        }`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => togglePasswordVisibility('current')}
                        disabled={changingPassword}
                      >
                        {showPasswords.current ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">
                      New Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="Enter your new password"
                        disabled={changingPassword}
                        className={`pr-10 ${
                          passwordValidation.isValid 
                            ? 'border-green-300' : 
                            passwordForm.newPassword && passwordForm.newPassword.length > 0 
                              ? 'border-red-300' : ''
                        }`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => togglePasswordVisibility('new')}
                        disabled={changingPassword}
                      >
                        {showPasswords.new ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {passwordForm.newPassword && (
                      <div className="mt-3">
                        <PasswordStrengthIndicator 
                          password={passwordForm.newPassword}
                          validation={passwordValidation}
                          showRequirements={true}
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirm New Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm your new password"
                        disabled={changingPassword}
                        className={`pr-10 ${
                          passwordMatchValidation.isValid && passwordForm.confirmPassword
                            ? 'border-green-300' : 
                            passwordForm.confirmPassword && passwordForm.confirmPassword.length > 0 
                              ? 'border-red-300' : ''
                        }`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => togglePasswordVisibility('confirm')}
                        disabled={changingPassword}
                      >
                        {showPasswords.confirm ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    {passwordForm.confirmPassword && !passwordMatchValidation.isValid && (
                      <p className="text-xs text-red-600">{passwordMatchValidation.error}</p>
                    )}
                    {passwordForm.confirmPassword && passwordMatchValidation.isValid && (
                      <p className="text-xs text-green-600">Passwords match perfectly!</p>
                    )}
                  </div>

                  {/* Additional Validation Messages */}
                  {passwordForm.currentPassword && passwordForm.newPassword && !currentPasswordDifferentValidation.isValid && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-700">{currentPasswordDifferentValidation.error}</p>
                    </div>
                  )}

                  {/* Password Change Button */}
                  <div className="pt-4">
                    <Button 
                      onClick={handlePasswordChange}
                      disabled={!isPasswordFormValid || changingPassword}
                      className="w-full sm:w-auto"
                    >
                      {changingPassword ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Account Security card */}
              <Card>
                <CardHeader>
                  <CardTitle>Administrator Security</CardTitle>
                  <CardDescription>Security information and recommendations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Administrator Account</p>
                      <p className="text-xs text-gray-500">You have full system access and privileges</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Security Recommendations</p>
                      <ul className="text-xs text-gray-500 mt-1 space-y-1">
                        <li>• Use a strong, unique password (12+ characters)</li>
                        <li>• Include uppercase, lowercase, numbers, and symbols</li>
                        <li>• Change your password regularly</li>
                        <li>• Never share your administrator credentials</li>
                        <li>• Always log out from shared devices</li>
                        <li>• Monitor system access logs regularly</li>
                        <li>• Use secure networks when accessing the admin panel</li>
                      </ul>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-3">
                    <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">System Protection</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Enhanced security policies are enforced for administrator accounts
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Admin Access Tab */}
            <TabsContent value="admin" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Database className="h-5 w-5" />
                    <CardTitle>Administrator Access</CardTitle>
                  </div>
                  <CardDescription>Your administrative privileges and system access</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* System Access */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">System Privileges</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="default" className="bg-green-600 text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Full System Access
                        </Badge>
                        <Badge variant="default" className="bg-blue-600 text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          User Management
                        </Badge>
                        <Badge variant="default" className="bg-purple-600 text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Data Analytics
                        </Badge>
                        <Badge variant="default" className="bg-indigo-600 text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          System Configuration
                        </Badge>
                      </div>
                    </div>

                    <Separator />

                    {/* Application Modules */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Application Modules</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                        {[
                          'Customer Management',
                          'Invoice System',
                          'Order Management',
                          'Purchase Orders',
                          'Delivery Challans',
                          'Credit Notes',
                          'Item Repository',
                          'Templates & Branding',
                          'E-Signatures',
                          'Payment Processing',
                          'Team Management',
                          'Organization Control'
                        ].map((module) => (
                          <Badge key={module} variant="outline" className="justify-start text-xs p-2">
                            <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                            {module}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Administrative Tools */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Administrative Tools</Label>
                      <div className="space-y-3 mt-2">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Settings className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">System Settings</span>
                          </div>
                          <Badge variant="default" className="bg-green-600">Enabled</Badge>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Database className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">Database Management</span>
                          </div>
                          <Badge variant="default" className="bg-green-600">Enabled</Badge>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Shield className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">Security Controls</span>
                          </div>
                          <Badge variant="default" className="bg-green-600">Enabled</Badge>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">User Administration</span>
                          </div>
                          <Badge variant="default" className="bg-green-600">Enabled</Badge>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <BarChart3 className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">Analytics & Reports</span>
                          </div>
                          <Badge variant="default" className="bg-green-600">Enabled</Badge>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Building className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">Organization Management</span>
                          </div>
                          <Badge variant="default" className="bg-green-600">Enabled</Badge>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* System Statistics */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">System Overview</Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center space-x-2">
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium text-blue-900">System Health</p>
                              <p className="text-xs text-blue-600">Excellent</p>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center space-x-2">
                            <Activity className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-green-900">Active Sessions</p>
                              <p className="text-xs text-green-600">1 SuperAdmin + {adminUsers.filter(a => a.status === 'active').length} Admins</p>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center space-x-2">
                            <Zap className="h-5 w-5 text-purple-600" />
                            <div>
                              <p className="text-sm font-medium text-purple-900">Performance</p>
                              <p className="text-xs text-purple-600">Optimal</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Security Status */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Security Status</Label>
                      <div className="space-y-2 mt-2">
                        <div className="flex items-center justify-between p-2 bg-green-50 rounded-md">
                          <div className="flex items-center space-x-2">
                            <Lock className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-green-700">RBAC System</span>
                          </div>
                          <Badge variant="outline" className="text-green-700 border-green-300">Active</Badge>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-blue-50 rounded-md">
                          <div className="flex items-center space-x-2">
                            <Globe className="h-4 w-4 text-blue-600" />
                            <span className="text-xs text-blue-700">SSL/TLS Encryption</span>
                          </div>
                          <Badge variant="outline" className="text-blue-700 border-blue-300">Active</Badge>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-purple-50 rounded-md">
                          <div className="flex items-center space-x-2">
                            <Shield className="h-4 w-4 text-purple-600" />
                            <span className="text-xs text-purple-700">Admin Session Protection</span>
                          </div>
                          <Badge variant="outline" className="text-purple-700 border-purple-300">Enabled</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* User Management Tab */}
            <TabsContent value="users" className="space-y-6">
              {/* Header with Create Admin Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Admin User Management</h2>
                  <p className="text-sm text-gray-500">Create and manage admin users with specific permissions</p>
                </div>
                <Dialog open={showCreateAdminDialog} onOpenChange={setShowCreateAdminDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => {
                      setEditingAdmin(null);
                      setNewAdminForm({
                        fullName: '',
                        email: '',
                        password: '',
                        confirmPassword: '',
                        permissions: [],
                        status: 'active'
                      });
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingAdmin ? 'Edit Admin User' : 'Create New Admin User'}
                      </DialogTitle>
                      <DialogDescription>
                        {editingAdmin 
                          ? 'Update admin user details and permissions' 
                          : 'Add a new admin user with specific permissions and access levels'
                        }
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                      {/* Basic Info */}
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="adminFullName">
                              Full Name <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="adminFullName"
                              value={newAdminForm.fullName}
                              onChange={(e) => setNewAdminForm(prev => ({ ...prev, fullName: e.target.value }))}
                              placeholder="Enter full name"
                              disabled={creatingAdmin}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="adminEmail">
                              Email Address <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="adminEmail"
                              type="email"
                              value={newAdminForm.email}
                              onChange={(e) => setNewAdminForm(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="Enter email address"
                              disabled={creatingAdmin || editingAdmin}
                              className={editingAdmin ? 'bg-gray-50' : ''}
                            />
                            {editingAdmin && (
                              <p className="text-xs text-gray-500">Email cannot be changed for existing users</p>
                            )}
                          </div>
                        </div>

                        {/* Password Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="adminPassword">
                              {editingAdmin ? 'New Password (optional)' : 'Password'} 
                              {!editingAdmin && <span className="text-destructive">*</span>}
                            </Label>
                            <div className="relative">
                              <Input
                                id="adminPassword"
                                type={showPasswords.newAdmin ? "text" : "password"}
                                value={newAdminForm.password}
                                onChange={(e) => setNewAdminForm(prev => ({ ...prev, password: e.target.value }))}
                                placeholder={editingAdmin ? "Enter new password (leave blank to keep current)" : "Enter password"}
                                disabled={creatingAdmin}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => togglePasswordVisibility('newAdmin')}
                                disabled={creatingAdmin}
                              >
                                {showPasswords.newAdmin ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                            {/* Password strength for new admin */}
                            {newAdminForm.password && (
                              <div className="mt-2">
                                <PasswordStrengthIndicator 
                                  password={newAdminForm.password}
                                  validation={newAdminPasswordValidation}
                                  showRequirements={false}
                                />
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="adminConfirmPassword">
                              {editingAdmin ? 'Confirm New Password' : 'Confirm Password'}
                              {(!editingAdmin || newAdminForm.password) && <span className="text-destructive">*</span>}
                            </Label>
                            <div className="relative">
                              <Input
                                id="adminConfirmPassword"
                                type={showPasswords.confirmAdmin ? "text" : "password"}
                                value={newAdminForm.confirmPassword}
                                onChange={(e) => setNewAdminForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                placeholder="Confirm password"
                                disabled={creatingAdmin}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => togglePasswordVisibility('confirmAdmin')}
                                disabled={creatingAdmin}
                              >
                                {showPasswords.confirmAdmin ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                            {newAdminForm.confirmPassword && !newAdminPasswordMatchValidation.isValid && (
                              <p className="text-xs text-red-600">{newAdminPasswordMatchValidation.error}</p>
                            )}
                            {newAdminForm.confirmPassword && newAdminPasswordMatchValidation.isValid && (
                              <p className="text-xs text-green-600">Passwords match!</p>
                            )}
                          </div>
                        </div>

                        {/* Status */}
                        <div className="space-y-2">
                          <Label>Account Status</Label>
                          <div className="flex items-center space-x-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="adminStatus"
                                value="active"
                                checked={newAdminForm.status === 'active'}
                                onChange={(e) => setNewAdminForm(prev => ({ ...prev, status: e.target.value }))}
                                disabled={creatingAdmin}
                                className="text-green-600"
                              />
                              <span className="text-sm">Active</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="adminStatus"
                                value="inactive"
                                checked={newAdminForm.status === 'inactive'}
                                onChange={(e) => setNewAdminForm(prev => ({ ...prev, status: e.target.value }))}
                                disabled={creatingAdmin}
                                className="text-gray-600"
                              />
                              <span className="text-sm">Inactive</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Permissions Section */}
                      <div className="space-y-4">
                        <div>
                         
                        </div>


                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-blue-600" />
                            <p className="text-sm text-blue-700 font-medium">Permission Guidelines</p>
                          </div>
                          <ul className="text-xs text-blue-600 mt-2 space-y-1 ml-6">
                            <li>• Admins cannot create or manage other admin users</li>
                            <li>• SuperAdmin privileges are not transferrable</li>
                            <li>• Permissions can be modified after account creation</li>
                            <li>• Users with inactive status cannot access the system</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowCreateAdminDialog(false);
                          setEditingAdmin(null);
                          setNewAdminForm({
                            fullName: '',
                            email: '',
                            password: '',
                            confirmPassword: '',
                            permissions: [],
                            status: 'active'
                          });
                        }}
                        disabled={creatingAdmin}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={editingAdmin ? handleUpdateAdmin : handleCreateAdmin}
                        disabled={!isNewAdminFormValid || creatingAdmin}
                      >
                        {creatingAdmin ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            {editingAdmin ? 'Updating...' : 'Creating...'}
                          </>
                        ) : (
                          <>
                            {editingAdmin ? (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Update Admin
                              </>
                            ) : (
                              <>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Admin
                              </>
                            )}
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Admin Users List */}
              <div className="space-y-4">
                {adminUsers.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <UserCog className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Admin Users</h3>
                      <p className="text-gray-500 mb-4">
                        You haven't created any admin users yet. Create your first admin to delegate system management.
                      </p>
                      <Button onClick={() => setShowCreateAdminDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Admin
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Admin Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Total Admins</p>
                              <p className="text-2xl font-bold text-blue-600">{adminUsers.length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Active</p>
                              <p className="text-2xl font-bold text-green-600">
                                {adminUsers.filter(admin => admin.status === 'active').length}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-5 w-5 text-gray-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Inactive</p>
                              <p className="text-2xl font-bold text-gray-600">
                                {adminUsers.filter(admin => admin.status === 'inactive').length}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-2">
                            <Activity className="h-5 w-5 text-purple-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Recent Logins</p>
                              <p className="text-2xl font-bold text-purple-600">
                                {adminUsers.filter(admin => admin.lastLogin && 
                                  new Date(admin.lastLogin) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                                ).length}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Admin Users Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {adminUsers.map((admin) => (
                        <AdminUserCard
                          key={admin.id}
                          admin={admin}
                          onEdit={handleEditAdmin}
                          onDelete={handleDeleteAdmin}
                          onToggleStatus={handleToggleAdminStatus}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* RBAC Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Shield className="h-5 w-5" />
                    <span>Role-Based Access Control (RBAC)</span>
                  </CardTitle>
                  <CardDescription>
                    Understanding the permission system and role hierarchy
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Role Hierarchy */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-3 block">Role Hierarchy</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                          <Crown className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="font-medium text-green-900">SuperAdmin</p>
                            <p className="text-xs text-green-700">Full system access + user management</p>
                          </div>
                        </div>
                        
                    
                        <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                          <User className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-blue-900">Admin</p>
                            <p className="text-xs text-blue-700">Module-specific permissions only</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Permission Categories */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-3 block">Permission Categories</Label>
                      <div className="space-y-2">
                        {[
                          { name: 'Business Operations', count: 6, color: 'blue' },
                          { name: 'Financial Management', count: 3, color: 'green' },
                          { name: 'System Administration', count: 3, color: 'purple' }
                        ].map((category) => (
                          <div key={category.name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm text-gray-700">{category.name}</span>
                            <Badge variant="outline" className={`text-${category.color}-700 border-${category.color}-300`}>
                              {category.count} modules
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Important Security Notes</p>
                        <ul className="text-xs text-amber-700 mt-2 space-y-1">
                          <li>• Only SuperAdmin can create, edit, or delete admin users</li>
                          <li>• Admin users cannot modify their own permissions</li>
                          <li>• All admin actions are logged for security auditing</li>
                          <li>• Disabled admin accounts retain their data but cannot access the system</li>
                          <li>• Password changes require current password verification</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </main>
    </>
  );
}